import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiClient, BranchActualVsPlanMetric, BranchAlertsMetric, BranchCapacityMetric, BranchPlanPercentageMetric, BranchRevenueMetric } from '../sdk/sdk';

@Injectable({ providedIn: 'root' })
export class BranchMetricsService {
  private readonly sdk = inject(ApiClient);

  getMonthlyRevenue(branchId: string, calendarStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchRevenue(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getWeeklyRevenue(branchId: string, weekStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchRevenue(branchId, 'weekly', this.toDateOnly(weekStartDate));
  }

  getWeeklyPlannedRevenue(branchId: string, weekStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchPlannedRevenue(branchId, 'weekly', this.toDateOnly(weekStartDate));
  }

  getCarryForwardRevenue(branchId: string, calendarStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchCarryForwardRevenue(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getRolloverRevenue(branchId: string, calendarStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchRolloverRevenue(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getPlannedRevenue(branchId: string, calendarStartDate: Date): Observable<BranchRevenueMetric> {
    return this.sdk.getBranchPlannedRevenue(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getPlanPercentage(branchId: string, calendarStartDate: Date): Observable<BranchPlanPercentageMetric> {
    return this.sdk.getBranchPlanPercentage(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getActualVsPlan(branchId: string, calendarStartDate: Date): Observable<BranchActualVsPlanMetric> {
    return this.sdk.getBranchActualVsPlan(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getMonthlyCapacity(branchId: string, calendarStartDate: Date): Observable<BranchCapacityMetric> {
    return this.sdk.getBranchCapacity(branchId, 'monthly', this.toFirstOfMonth(calendarStartDate));
  }

  getWeeklyCapacity(branchId: string, weekStartDate: Date): Observable<BranchCapacityMetric> {
    return this.sdk.getBranchCapacity(branchId, 'weekly', this.toDateOnly(weekStartDate));
  }

  getWeeklyAlerts(branchId: string, weekStartDate: Date): Observable<BranchAlertsMetric[]> {
    return this.sdk.getBranchAlerts(branchId, 'weekly', this.toDateOnly(weekStartDate));
  }

  /**
   * The calendar's start date is the Monday on or before the 1st of the month.
   * Adding 7 days guarantees we land within the target month, then we take the 1st.
   * Returns a Date with toISOString overridden to produce a date-only string (YYYY-MM-DD)
   * since the SDK serializes path parameters via toISOString() but the server expects DateOnly.
   */
  private toFirstOfMonth(calendarStartDate: Date): Date {
    const inMonth = new Date(calendarStartDate.getFullYear(), calendarStartDate.getMonth(), calendarStartDate.getDate() + 7);
    const firstOfMonth = new Date(inMonth.getFullYear(), inMonth.getMonth(), 1);
    return this.toDateOnly(firstOfMonth);
  }

  private toDateOnly(date: Date): Date {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    date.toISOString = () => `${year}-${month}-${day}`;
    return date;
  }
}
